import { ModalTariffSelectTableProps } from 'components/common/ModalComponents/ModalTariffSelectTable';
import {
  afterHoursTariffCodes,
  hospitalCarePremium,
} from 'constants/tariffConstants';
import type { Claim, Debit, Tariff } from 'interfaces';
import { calculateLocationServiceRate } from './overheadUtils';
import { isUnreconciledTariffStatus } from './claimStatusUtils';
import { isAnesthesiaSpecialty } from '@billing/validation/specialty';
import type { Province } from '@billing/validation/payor';
import { CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';

const HOSPITAL_CARE_PREMIUM_MULTIPLIER = 1.15;
const HOSPITAL_PREMIUM_SERVICE_LOCATION_INDICATORS = new Set([
  undefined,
  null,
  '',
  ' ',
  'E',
]);

export const shouldApplyHospitalCarePremium = ({
  hospitalCode,
  serviceLocationIndicator,
}: {
  hospitalCode?: string | null;
  serviceLocationIndicator?: string | null;
}) => {
  if (!hospitalCode) return false;

  return HOSPITAL_PREMIUM_SERVICE_LOCATION_INDICATORS.has(
    serviceLocationIndicator
  );
};

const getHospitalCarePremiumMultiplier = (
  tariff: Tariff,
  hospitalCode: string | null,
  serviceLocationIndicator: string | null
) =>
  shouldApplyHospitalCarePremium({
    hospitalCode,
    serviceLocationIndicator,
  }) && hospitalCarePremium.has(String(tariff.code ?? '').trim())
    ? HOSPITAL_CARE_PREMIUM_MULTIPLIER
    : 1;

const isAfterHoursTariffCode = (code: string | null | undefined) =>
  afterHoursTariffCodes.has((code ?? '').trim());

const getBillingUnits = (tariff: Tariff, useAnesthesiaBilling: boolean) => {
  if (useAnesthesiaBilling) {
    return Number(tariff.anesthesiaUnits ?? 0);
  }
  return Number((tariff as any).units);
};

const getAnesthesiaServiceValue = (tariff: Tariff) => {
  const anesthesiaServiceValue = Number(tariff.anesthesiaServiceValue ?? 0);
  return Number.isFinite(anesthesiaServiceValue) && anesthesiaServiceValue !== 0
    ? anesthesiaServiceValue
    : null;
};

const roundCurrency = (value: number) =>
  Math.round((value + Number.EPSILON) * 100) / 100;

/**
 * Calculates tariff totals by applying a location modifier to eligible base tariffs
 * (taking into account each tariff's units) and then calculating modifier tariffs
 * (also respecting units) based on either the pre- or post-differential base totals.
 *
 * @param {Tariff[]} tariffs - An array of tariff objects, each containing:
 *                              - type: 'Base' | 'Modifier'
 *                              - rate: string | number  (e.g. '10.00' or 10.00)
 *                              - units: number          (scalar multiplier, e.g. 2)
 * @param {number} locationModifier - A numeric modifier applied to the base tariffs.
 * @param {'physical' | 'virtual'} claimLocation - Which claim location mode
 *                                  applies to the fee differential logic.
 * @returns {Tariff[]} - An array of tariff objects with updated `rate` (as a string
 *                       with two decimals) after applying units and the location modifier.
 * @throws {Error} NullTariffError      - If the `tariffs` parameter is null or undefined.
 * @throws {Error} TariffRateNaNError   - If any tariff rate is not a valid number.
 * @throws {Error} TariffUnitsNaNError  - If any tariff units is not a valid number.
 */

export const calculateTariffTotals = (
  tariffs: Tariff[],
  locationModifier: number,
  hospitalCode: string | null,
  serviceLocationIndicator: string | null,
  claimLocation: 'physical' | 'virtual' = 'physical',
  providerSpecialtyCode?: string | null,
  province?: Province | null
): Tariff[] => {
  if (!tariffs) {
    throw new Error('NullTariffError');
  }

  // Separate base and modifier tariffs
  const baseTariffs = tariffs.filter((t) => t.type === 'Base');
  const modifierTariffs = tariffs.filter((t) => t.type === 'Modifier');

  let sumOfPreDifferentialBaseFees = 0;
  let sumOfPostDifferentialBaseFees = 0;
  const result: Tariff[] = [];

  // First, handle all base tariffs and apply the location modifier when eligible.
  baseTariffs.forEach((tariff) => {
    const anesthesiaServiceValue = isAnesthesiaSpecialty(
      province,
      providerSpecialtyCode
    )
      ? getAnesthesiaServiceValue(tariff)
      : null;
    const rawRate = Number(tariff.originalRate ?? tariff.rate);
    const billingRate = anesthesiaServiceValue ?? rawRate;
    const rawUnits = getBillingUnits(tariff, anesthesiaServiceValue != null);
    const percentage = (tariff?.percentage ?? 100) / 100;
    const premiumMultiplier = getHospitalCarePremiumMultiplier(
      tariff,
      hospitalCode,
      serviceLocationIndicator
    );
    const shouldApplyLocationModifier =
      anesthesiaServiceValue == null && tariff.applyLocationModifier !== false;
    const effectiveLocationModifier = shouldApplyLocationModifier
      ? locationModifier
      : 1;

    if (isNaN(rawRate)) {
      throw new Error('TariffRateNaNError');
    }
    if (isNaN(rawUnits)) {
      throw new Error('TariffUnitsNaNError');
    }

    const base =
      billingRate *
      rawUnits *
      (anesthesiaServiceValue == null ? premiumMultiplier : 1);

    // Anesthesia tariffs are billed directly from their service value and
    // time-derived anesthesia units, without other fee adjustments.
    const adjustedBase = roundCurrency(base * effectiveLocationModifier);
    const total = roundCurrency(
      adjustedBase * (anesthesiaServiceValue == null ? percentage : 1)
    );
    const hasManualTotal =
      tariff.isManualTotal === true && tariff.total != null;
    const effectiveTotal = hasManualTotal ? Number(tariff.total) : total;

    result.push({
      ...tariff,
      units: anesthesiaServiceValue == null ? rawUnits : tariff.units,
      rate: rawRate.toFixed(2),
      total: effectiveTotal,
    });

    if (hasManualTotal) {
      sumOfPreDifferentialBaseFees += effectiveTotal;
      sumOfPostDifferentialBaseFees += effectiveTotal;
    } else {
      sumOfPreDifferentialBaseFees += roundCurrency(
        base * (anesthesiaServiceValue == null ? percentage : 1)
      );
      sumOfPostDifferentialBaseFees += total;
    }
  });

  // Virtual after-hours modifiers use pre-differential base totals.
  // All other modifiers use post-differential base totals.
  modifierTariffs.forEach((tariff) => {
    const rawRate = Number(tariff.rate);
    const rawUnits = getBillingUnits(tariff, false);
    const percentage = (tariff?.percentage ?? 100) / 100;
    const premiumMultiplier = getHospitalCarePremiumMultiplier(
      tariff,
      hospitalCode,
      serviceLocationIndicator
    );

    if (isNaN(rawRate)) {
      throw new Error('TariffRateNaNError');
    }
    if (isNaN(rawUnits)) {
      throw new Error('TariffUnitsNaNError');
    }

    const modifierBaseTotal =
      claimLocation === 'virtual' && isAfterHoursTariffCode(tariff.code)
        ? sumOfPreDifferentialBaseFees
        : sumOfPostDifferentialBaseFees;

    const adjustedModifier = roundCurrency(
      rawRate * rawUnits * modifierBaseTotal * premiumMultiplier
    );

    result.push({
      ...tariff,
      rate: rawRate.toFixed(2),
      total: roundCurrency(adjustedModifier * percentage),
    });
  });
  return result;
};

type MergeTariff = Tariff & { _rowID?: string };

const getTariffMergeKeys = (tariff: MergeTariff): string[] => {
  const tariffKey = `tariff:${tariff.id}`;
  if (!tariff._rowID) return [tariffKey];
  return [tariffKey, `row:${tariff._rowID}`];
};

export const mergeTariffs = (
  sharedTariffs: MergeTariff[],
  claimTariffs: MergeTariff[],
  sharedICD9?: string
): Tariff[] => {
  const claimTariffsByKey = new Map<string, MergeTariff>();
  for (const tariff of claimTariffs || []) {
    for (const key of getTariffMergeKeys(tariff)) {
      if (!claimTariffsByKey.has(key)) claimTariffsByKey.set(key, tariff);
    }
  }
  const seenKeys = new Set<string>();
  const result: Tariff[] = [];

  const addTariff = (tariff: MergeTariff, icd9?: string) => {
    const keys = getTariffMergeKeys(tariff);
    if (keys.some((key) => seenKeys.has(key))) return;
    keys.forEach((key) => seenKeys.add(key));
    result.push(icd9 ? { ...tariff, icd9 } : tariff);
  };

  for (const tariff of sharedTariffs || []) {
    const [tariffKey, rowKey] = getTariffMergeKeys(tariff);
    const claimRow = rowKey ? claimTariffsByKey.get(rowKey) : undefined;
    // Selecting another tariff keeps the inherited row ID. Treat it as a
    // claim-specific replacement rather than reintroducing the shared tariff.
    const mergedTariff =
      claimRow && claimRow.id !== tariff.id ? claimRow : tariff;
    const icd9 =
      sharedICD9 ||
      mergedTariff.icd9 ||
      claimRow?.icd9 ||
      claimTariffsByKey.get(tariffKey)?.icd9 ||
      undefined;
    addTariff(mergedTariff, icd9);
  }

  for (const tariff of claimTariffs || []) {
    addTariff(tariff);
  }

  return result;
};

export const applyTariffTotalsWithOverrides = (
  tariffs: Tariff[],
  debits: Debit[] = [],
  locationModifier = 1,
  hospitalCode: string | null = null,
  serviceLocationIndicator: string | null = null,
  claimLocation: 'physical' | 'virtual' = 'physical',
  providerSpecialtyCode?: string | null,
  province?: Province | null
): Tariff[] => {
  const computed = calculateTariffTotals(
    tariffs,
    locationModifier,
    hospitalCode,
    serviceLocationIndicator,
    claimLocation,
    providerSpecialtyCode,
    province
  ) as Tariff[];
  const tariffLookup = new Map<string, Tariff>();
  tariffs.forEach((tariff) => {
    tariffLookup.set(tariff.id, tariff);
  });

  return computed.map((tariff) => {
    const source = tariffLookup.get(tariff.id);
    const relatedDebit = debits.find((d) => d.tariffID === tariff.id);

    const isReconciled =
      relatedDebit?.paidAmount &&
      relatedDebit?.requestedAmount &&
      relatedDebit.paidAmount === relatedDebit.requestedAmount;

    if (isReconciled) {
      return { ...tariff, total: Number(relatedDebit.paidAmount) } as Tariff;
    }

    if (source?.isManualTotal === true && source.total != null) {
      return { ...tariff, total: source.total };
    }

    return tariff;
  });
};

export const applyClaimTariffTotalsWithOverrides = (
  claim: Claim,
  debits: Debit[] = []
): Tariff[] => {
  const { modifier, claimLocation } = calculateLocationServiceRate(
    claim.virtualPhysicianRegion,
    claim.locationOfService,
    claim.payor?.province
  );

  return applyTariffTotalsWithOverrides(
    claim.tariffs as Tariff[],
    debits,
    modifier || 1,
    claim.hospitalCode,
    claim.serviceLocationIndicator,
    claimLocation,
    claim.provider?.specialtyCode,
    claim.payor?.province
  );
};

export const sumTariffTotals = (tariffs: Array<Pick<Tariff, 'total'>> = []) =>
  tariffs.reduce((acc, row) => acc + Number(row.total ?? 0), 0);

const formatLocationModifierPercent = (modifier: number) =>
  Number(((modifier - 1) * 100).toFixed(1));

const formatLocationModifierMultiplier = (modifier: number) =>
  modifier.toFixed(3).replace(/\.?0+$/, '');

export const formatLocationModifierLabel = (
  locationServiceRate: ModalTariffSelectTableProps['locationServiceRate']
) => {
  const percent = formatLocationModifierPercent(locationServiceRate.modifier);
  const sign = percent >= 0 ? '+' : '';

  return `${sign}${percent}% (code ${
    locationServiceRate.code
  }, x${formatLocationModifierMultiplier(locationServiceRate.modifier)})`;
};

export const getLocationModifierTooltipLabel = (
  locationServiceRate: ModalTariffSelectTableProps['locationServiceRate']
) => {
  const percent = formatLocationModifierPercent(locationServiceRate.modifier);
  const sign = percent >= 0 ? '+' : '';

  return `Applied location fee differential: ${sign}${percent}% using modifier code ${locationServiceRate.code}.`;
};

export const resolveTariffRequestedAmount = (
  tariff: Tariff,
  status?: string | null,
  debit?: Debit
): number => {
  const tariffTotal = Number(tariff.total ?? 0);
  const isUnreconciled = isUnreconciledTariffStatus(status);

  if (status === CLAIM_TARIFF_STATUS.PAID) {
    return Number(debit?.requestedAmount ?? tariffTotal);
  }

  if (isUnreconciled) {
    return Number(debit?.requestedAmount ?? tariffTotal);
  }

  if (tariff.isManualTotal && tariff.total != null) {
    return tariff.total;
  }

  return tariffTotal;
};
